import { useEffect } from 'react';
import { Box, MenuItem, useTheme } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber, isValidWebsite } from '../../../common/utils/formUtils';
import {
  ErrorAlert, IFormInputs, StyledSelect, styledSelectMenuProps, StyledTextField,
} from '../../../common/StyledFormInputs';
import { TestIds } from '../../../assets/testIds';
import { FormFieldBox } from '../../../common/FormFieldBox';
import { AddressAutoCompleteInput } from '../../../common/AddressAutoCompleteInput';
import { ValidFieldPattern } from '../../Onboarding/constants';
import { CompanyVehicleQuestionOptions } from '../Partner/Enrollment/PartnerSpecificFormConfig';
import { useAppSelector } from '../../../store/hooks';
import { selectMemberInformation } from '../../../store/slices/onboardingSlice';

export function AccountSettingsCompanyFormControl() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    register, control, formState, getFieldState,
    reset, getValues,
  } = useFormContext<IFormInputs>();
  const { errors } = formState;
  const storeMemberInformation = useAppSelector(selectMemberInformation);

  useEffect(() => {
    if (storeMemberInformation?.company?.partnerEnrollmentQuestions) {
      const currentValues = getValues();
      reset({
        ...currentValues,
        companyConfig: {
          partnerEnrollmentQuestions: storeMemberInformation.company.partnerEnrollmentQuestions,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeMemberInformation, reset]);

  const addrState = getFieldState('businessInfo.autoAddress', formState);
  const addressFromDatabase = addrState.isTouched || addrState.isDirty;

  return (
    <Box
      sx={{
        maxWidth: theme.spacing(64),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: theme.spacing(64),
        [theme.breakpoints.down('sm')]: {
          width: '100%',
        },
      }}
    >
      {/* Company Name Field */}
      <FormFieldBox fieldName={t('onboarding.questionPlaceholder.Company Name')} isRequired={true}>
        <>
          <StyledTextField
            sx={{ marginBottom: theme.spacing(0) }}
            data-testid={TestIds.accountSettingsPage.companyNameTxt}
            placeholder='Name'
            error={!!errors.businessInfo?.name}
            {...register('businessInfo.name', { required: true })}
          />
          {errors.businessInfo?.name && <ErrorAlert />}
        </>
      </FormFieldBox>
      {/* Company Address Field */}
      <FormFieldBox fieldName={t('onboarding.account.companyBusinessAddress')} isRequired={true}>
        <>
          <Box sx={{ width: '100%' }}>
            <AddressAutoCompleteInput
              data-testid={TestIds.accountSettingsPage.companyAddressTxt}
              formFieldName={'businessInfo.autoAddress'}
              placeholderText={'Business address'}
              optionalStyles={{
                marginBottom: theme.spacing(0),
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.palette.common.white,
                  borderRadius: theme.spacing(1.5),
                },
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1px',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[300],
                  borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.grey[300],
                  borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '2px',
                },
              }}
            />
          </Box>
          {errors.businessInfo?.autoAddress && (
            <ErrorAlert
              errorMessage={
                (addressFromDatabase) ?
                  `${t('error.addressField.required')}` :
                  `${t('error.addressField.confirm')}`
              }
            />
          )}
        </>
      </FormFieldBox>
      {/* Company Email Field */}
      <FormFieldBox fieldName={t('onboarding.questionPlaceholder.Company Email')} isRequired={true}>
        <>
          <StyledTextField
            sx={{ marginBottom: theme.spacing(0) }}
            data-testid={TestIds.accountSettingsPage.companyEmailTxt}
            placeholder='Email'
            error={!!errors.businessInfo?.email}
            {...register('businessInfo.email', {
              required: true,
              pattern: {
                value: ValidFieldPattern.validEmailPattern,
                message: `${t('error.invalidEmail2')}`,
              },
            })}
          />
          {errors.businessInfo?.email &&
            <ErrorAlert
              errorMessage={errors.businessInfo?.email?.message}
            />}
        </>
      </FormFieldBox>
      {/* Company Phone Number Field */}
      <FormFieldBox fieldName={t('onboarding.questionPlaceholder.Company Phone Number')} isRequired={true}>
        <>
          <StyledTextField
            sx={{ marginBottom: theme.spacing(0) }}
            data-testid={TestIds.accountSettingsPage.companyPhoneTxt}
            placeholder='Phone'
            error={!!errors.businessInfo?.phone}
            {...register('businessInfo.phone', {
              required: true,
              validate: (value) => (value && isValidPhoneNumber(value)) || `${t('error.invalidPhoneNumber')}`,
            })}
          />
          {errors.businessInfo?.phone &&
            <ErrorAlert
              errorMessage={errors.businessInfo?.phone?.message}
            />}
        </>
      </FormFieldBox>
      {/* Company Website Field */}
      <FormFieldBox fieldName={t('onboarding.questionPlaceholder.Company Website')} isRequired={false}>
        <>
          <StyledTextField
            sx={{
              marginBottom: theme.spacing(0),
            }}
            data-testid={TestIds.accountSettingsPage.companyWebsiteTxt}
            placeholder='Website'
            error={!!errors.businessInfo?.website}
            {...register('businessInfo.website', {
              required: false,
              validate: (value) => {
                if (!value) return true; // Empty is okay
                return isValidWebsite(value) ? true : t('error.invalidWebsiteUrl') as string;
              },
            })}
          />
          {errors.businessInfo?.website &&
            <ErrorAlert
              optionalStyles={{ marginBottom: theme.spacing(2) }}
              errorMessage={errors.businessInfo?.website?.message}
            />}
        </>
      </FormFieldBox>
      {/* Company Vehicle ques Field */}
      <FormFieldBox fieldName={t('onboarding.questionPlaceholder.companyVehicleQuestion')} isRequired={false}>
        <>
          <Controller
            name="companyConfig.partnerEnrollmentQuestions.questions.number_of_vehicles.answer"
            control={control}
            render={({ field, fieldState }) => (
              <StyledSelect
                {...field}
                sx={{
                  marginBottom: theme.spacing(2),
                }}
                displayEmpty
                value={field.value ?? ''}
                data-testid={TestIds.accountSettingsPage.companyVehicleTxt}
                error={!!fieldState.error}
                MenuProps={{
                  ...styledSelectMenuProps,
                  anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                }}
              >
                {!field.value && (
                  <MenuItem disabled value=''>
                    {t('onboarding.questionPlaceholder.selectOption')}
                  </MenuItem>
                )}
                {CompanyVehicleQuestionOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </StyledSelect>
            )}
          />
        </>
      </FormFieldBox>
    </Box>
  );
}
